import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

const CM_PER_INCH = 2.54;
const UNITS = ['cm', 'in'];
const UNIT_STORAGE_KEY = 'size-chart-unit';
const NUMBER_PATTERN = /\d+(?:[.,]\d+)?/g;

/**
 * @typedef {object} SizeChartColumn
 * @property {string} key - Key used to read the cell when rows are objects.
 * @property {string} label - Header text.
 * @property {boolean} convert - Whether numeric cells in this column are converted between cm and in.
 *
 * @typedef {object} SizeChartTable
 * @property {string} title
 * @property {SizeChartColumn[]} columns
 * @property {string[][]} rows
 *
 * @typedef {object} SizeChart
 * @property {string} title
 * @property {string} description
 * @property {string | null} unit - Unit the values are written in ('cm' | 'in'), or null when not convertible.
 * @property {boolean} toggle - Whether to show the cm/in toggle.
 * @property {SizeChartTable[]} tables
 * @property {{ title: string, text: string }[]} guide
 * @property {string[]} notes
 * @property {string | null} image
 */

/**
 * A "Sizing guide" trigger that opens a modal size chart rendered from a JSON product metafield.
 *
 * The chart is rendered each time the dialog opens, since the variant picker morphs its markup
 * (and the selected size) whenever the variant changes.
 */
class SizeChartComponent extends HTMLElement {
  /** @type {string | null} */
  #unit = null;

  #previousScrollY = 0;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keydown', this.#handleKeyDown);
    // `cancel` and `close` don't bubble, so listen in the capture phase.
    this.addEventListener('cancel', this.#handleCancel, true);
    this.addEventListener('close', this.#handleClose, true);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('cancel', this.#handleCancel, true);
    this.removeEventListener('close', this.#handleClose, true);
    if (this.dialog) unlockScroll(this.dialog);
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }

  showDialog() {
    const { dialog } = this;
    if (!dialog || dialog.open) return;

    this.#render();
    this.#previousScrollY = getScrollTop();

    requestAnimationFrame(() => {
      lockScroll(dialog);
      dialog.showModal();
    });
  }

  closeDialog = async () => {
    const { dialog } = this;
    if (!dialog?.open) return;

    // Restart the closing animation from a clean state
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };

  /**
   * @param {string} unit
   */
  setUnit(unit) {
    if (!UNITS.includes(unit)) return;

    this.#unit = unit;
    try {
      localStorage.setItem(UNIT_STORAGE_KEY, unit);
    } catch {
      // Storage can be unavailable (private mode, blocked cookies)
    }
    this.#applyUnit();
  }

  /**
   * Clicks are handled here (not with `on:click`) and stopped from propagating so a size chart
   * nested inside another dialog (e.g. quick add) doesn't close its parent.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { target } = event;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-size-chart-trigger]')) {
      event.preventDefault();
      this.showDialog();
      return;
    }

    const { dialog } = this;
    if (!dialog?.open || !dialog.contains(target)) return;

    event.stopPropagation();

    /** @type {HTMLElement | null} */
    const unitButton = target.closest('[data-size-chart-unit]');

    if (target.closest('[data-size-chart-close]')) {
      this.closeDialog();
    } else if (unitButton) {
      this.setUnit(unitButton.dataset.sizeChartUnit ?? '');
    } else if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.dialog?.open) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog();
  };

  /**
   * Replace the native (unanimated) escape close with the animated one.
   *
   * @param {Event} event
   */
  #handleCancel = (event) => {
    if (event.target !== this.dialog) return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * @param {Event} event
   */
  #handleClose = (event) => {
    const { dialog } = this;
    if (!dialog || event.target !== dialog) return;

    unlockScroll(dialog);
    scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  };

  #render() {
    const body = this.querySelector('[data-size-chart-body]');
    const chart = this.#readChart();
    if (!body || !chart) return;

    const heading = this.querySelector('[data-size-chart-title]');
    if (heading && chart.title) heading.textContent = chart.title;

    this.#unit ??= getStoredUnit() ?? chart.unit;

    const selectedValue = normalize(this.dataset.selectedValue);
    const { labelUnits = '', labelGuide = '' } = this.dataset;

    /** @type {Node[]} */
    const content = [];

    if (chart.description) content.push(createElement('p', 'size-chart__description', chart.description));

    if (chart.unit && chart.toggle) {
      const toggle = createElement('div', 'size-chart__units');
      toggle.setAttribute('role', 'group');
      toggle.setAttribute('aria-label', labelUnits);

      for (const unit of UNITS) {
        const button = createElement('button', 'size-chart__unit button-unstyled', unit);
        button.type = 'button';
        button.dataset.sizeChartUnit = unit;
        toggle.append(button);
      }

      content.push(toggle);
    }

    for (const table of chart.tables) {
      content.push(renderTable(table, chart.unit, selectedValue));
    }

    if (chart.image) {
      const image = createElement('img', 'size-chart__image');
      image.src = chart.image;
      image.alt = '';
      image.loading = 'lazy';
      content.push(image);
    }

    if (chart.guide.length) {
      const guide = createElement('div', 'size-chart__guide');
      guide.append(createElement('h3', 'size-chart__subtitle h6', labelGuide));

      const list = createElement('dl', 'size-chart__guide-list');
      for (const item of chart.guide) {
        if (item.title) list.append(createElement('dt', '', item.title));
        list.append(createElement('dd', '', item.text));
      }

      guide.append(list);
      content.push(guide);
    }

    if (chart.notes.length) {
      const notes = createElement('ul', 'size-chart__notes');
      for (const note of chart.notes) notes.append(createElement('li', '', note));
      content.push(notes);
    }

    body.replaceChildren(...content);
    this.#applyUnit();
  }

  /**
   * Updates the unit toggle state and every convertible cell to the current unit.
   */
  #applyUnit() {
    const unit = this.#unit;

    for (const button of this.querySelectorAll('[data-size-chart-unit]')) {
      button.setAttribute('aria-pressed', String(button.getAttribute('data-size-chart-unit') === unit));
    }

    for (const cell of this.querySelectorAll('[data-size-chart-value]')) {
      const value = cell.getAttribute('data-size-chart-value') ?? '';
      const from = cell.getAttribute('data-size-chart-from') ?? '';
      cell.textContent = unit ? convert(value, from, unit) : value;
    }
  }

  /** @returns {SizeChart | null} */
  #readChart() {
    const script = this.querySelector('script[data-size-chart-data]');
    if (!script?.textContent) return null;

    try {
      let data = JSON.parse(script.textContent);
      // Multi-line text metafields holding JSON come through as a string
      if (typeof data === 'string') data = JSON.parse(data);
      return normalizeChart(data);
    } catch (error) {
      console.warn('[size-chart] Invalid size chart JSON', error);
      return null;
    }
  }
}

if (!customElements.get('size-chart-component')) customElements.define('size-chart-component', SizeChartComponent);

/**
 * @param {SizeChartTable} table
 * @param {string | null} unit
 * @param {string} selectedValue
 * @returns {HTMLElement}
 */
function renderTable(table, unit, selectedValue) {
  const wrapper = createElement('div', 'size-chart__table-block');

  if (table.title) wrapper.append(createElement('h3', 'size-chart__subtitle h6', table.title));

  const scroller = createElement('div', 'size-chart__table-wrapper');
  scroller.tabIndex = 0;

  const tableElement = createElement('table', 'size-chart__table');

  if (table.columns.some((column) => column.label)) {
    const headRow = createElement('tr');
    for (const column of table.columns) {
      const th = createElement('th', '', column.label);
      th.scope = 'col';
      headRow.append(th);
    }
    tableElement.append(createElement('thead', '', headRow));
  }

  const tbody = createElement('tbody');

  for (const row of table.rows) {
    const tr = createElement('tr');
    if (selectedValue && normalize(row[0]) === selectedValue) tr.classList.add('is-selected');

    row.forEach((value, index) => {
      const cell = createElement(index === 0 ? 'th' : 'td', '', value);
      if (cell instanceof HTMLTableCellElement && index === 0) cell.scope = 'row';

      if (unit && table.columns[index]?.convert) {
        cell.setAttribute('data-size-chart-value', value);
        cell.setAttribute('data-size-chart-from', unit);
      }

      tr.append(cell);
    });

    tbody.append(tr);
  }

  tableElement.append(tbody);
  scroller.append(tableElement);
  wrapper.append(scroller);

  return wrapper;
}

/**
 * Accepts either the full chart object, or a bare array of rows.
 *
 * @param {any} raw
 * @returns {SizeChart | null}
 */
function normalizeChart(raw) {
  if (Array.isArray(raw)) raw = { rows: raw };
  if (!raw || typeof raw !== 'object') return null;

  const singleTable = { columns: raw.columns ?? raw.headers, rows: raw.rows ?? raw.data };

  const tables = (Array.isArray(raw.tables) ? raw.tables : [singleTable])
    .map((table) => normalizeTable(table, raw.convert !== false))
    .filter((table) => table.rows.length);

  if (!tables.length) return null;

  const unit = String(raw.unit ?? '').toLowerCase();

  return {
    title: toText(raw.title),
    description: toText(raw.description),
    unit: UNITS.includes(unit) ? unit : null,
    toggle: raw.unit_toggle !== false,
    tables,
    guide: toArray(raw.how_to_measure ?? raw.guide).map((item) =>
      item && typeof item === 'object'
        ? { title: toText(item.title ?? item.label), text: toText(item.text ?? item.description) }
        : { title: '', text: toText(item) }
    ),
    notes: toArray(raw.notes ?? raw.note).map(toText).filter(Boolean),
    image: typeof raw.image === 'string' && raw.image ? raw.image : null,
  };
}

/**
 * Rows can be arrays (matched to `columns` by position) or objects (matched by column label/key).
 * The first column holds the size name and is never converted.
 *
 * @param {any} table
 * @param {boolean} chartConvert
 * @returns {SizeChartTable}
 */
function normalizeTable(table, chartConvert) {
  const rawRows = toArray(table?.rows ?? table?.data);
  let rawColumns = toArray(table?.columns ?? table?.headers);

  const firstRow = rawRows[0];
  const objectRows = firstRow && typeof firstRow === 'object' && !Array.isArray(firstRow);

  if (!rawColumns.length) {
    rawColumns = objectRows
      ? Object.keys(firstRow)
      : Array.from({ length: Math.max(0, ...rawRows.map((row) => toArray(row).length)) }, () => '');
  }

  /** @type {SizeChartColumn[]} */
  const columns = rawColumns.map((column, index) => {
    const isObject = column && typeof column === 'object';
    const label = toText(isObject ? column.label ?? column.name : column);

    return {
      key: isObject ? toText(column.key ?? label) : label,
      label,
      convert: chartConvert && table?.convert !== false && index > 0 && !(isObject && column.convert === false),
    };
  });

  const rows = rawRows.map((row) =>
    objectRows ? columns.map((column) => toText(row?.[column.key])) : toArray(row).map(toText)
  );

  return { title: toText(table?.title), columns, rows };
}

/**
 * Converts every number in a cell, so ranges like "82-86" work too.
 *
 * @param {string} value
 * @param {string} from
 * @param {string} to
 * @returns {string}
 */
function convert(value, from, to) {
  if (from === to || !UNITS.includes(from) || !UNITS.includes(to)) return value;

  const factor = from === 'cm' ? 1 / CM_PER_INCH : CM_PER_INCH;

  return value.replace(NUMBER_PATTERN, (match) => {
    const converted = Number.parseFloat(match.replace(',', '.')) * factor;
    return String(Math.round(converted * 10) / 10);
  });
}

/** @returns {string | null} */
function getStoredUnit() {
  try {
    const unit = localStorage.getItem(UNIT_STORAGE_KEY);
    return unit && UNITS.includes(unit) ? unit : null;
  } catch {
    return null;
  }
}

/**
 * @template {keyof HTMLElementTagNameMap} K
 * @param {K} tag
 * @param {string} [className]
 * @param {string | Node} [content]
 * @returns {HTMLElementTagNameMap[K]}
 */
function createElement(tag, className = '', content) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (typeof content === 'string') element.textContent = content;
  else if (content) element.append(content);
  return element;
}

/**
 * @param {any} value
 * @returns {any[]}
 */
function toArray(value) {
  if (value == null || value === '') return [];
  return Array.isArray(value) ? value : [value];
}

/**
 * @param {any} value
 * @returns {string}
 */
function toText(value) {
  return value == null ? '' : String(value).trim();
}

/**
 * @param {any} value
 * @returns {string}
 */
function normalize(value) {
  return toText(value).toLowerCase();
}
